export interface TodoDayMarkerCompletedItem {
    id: string;
    categoryId?: string | null;
    category?: string | null;
    color?: string | null;
    label?: string | null;
    kindLabel?: string | null;
    routineId?: string | null;
}

export interface TodoDayMarkerCategory {
    id?: string | null;
    name?: string | null;
    color?: string | null;
}

export interface TodoDayMarkerSlot {
    index: number;
    filled: boolean;
    color: string;
    sourceId?: string;
    categoryId: string | null;
    routineId: string | null;
    label: string;
    summary: string;
}

export const TODO_DAY_MARKER_SLOT_COUNT = 4;
export const TODO_DAY_MARKER_NEUTRAL_COLOR = '#d5dadd';

export function buildTodoDayMarkerSlots(
    date: string,
    completedItems: TodoDayMarkerCompletedItem[],
    categories: TodoDayMarkerCategory[],
    neutralColor = TODO_DAY_MARKER_NEUTRAL_COLOR
): TodoDayMarkerSlot[] {
    void date;
    const items = Array.isArray(completedItems) ? completedItems : [];
    const categoryLookup = buildCategoryLookup(categories);

    return Array.from({ length: TODO_DAY_MARKER_SLOT_COUNT }, (_, index) => {
        const item = items[index] || null;
        if (!item) return emptyTodoDayMarkerSlot(index, neutralColor);
        return filledTodoDayMarkerSlot(index, item, categoryLookup, neutralColor);
    });
}

function emptyTodoDayMarkerSlot(index: number, neutralColor: string): TodoDayMarkerSlot {
    return {
        index,
        filled: false,
        color: neutralColor,
        categoryId: null,
        routineId: null,
        label: '빈 슬롯',
        summary: `${index + 1}번 슬롯: 완료 없음`
    };
}

function filledTodoDayMarkerSlot(
    index: number,
    item: TodoDayMarkerCompletedItem,
    categoryLookup: Map<string, TodoDayMarkerCategory>,
    neutralColor: string
): TodoDayMarkerSlot {
    const category = findCategoryForItem(item, categoryLookup);
    const color = item.color || category?.color || neutralColor;
    const label = item.label || category?.name || item.category || '할 일';
    const kindLabel = item.kindLabel || '할 일';

    return {
        index,
        filled: true,
        color,
        sourceId: item.id,
        categoryId: category?.id || item.categoryId || null,
        routineId: item.routineId || null,
        label,
        summary: `${index + 1}번 슬롯: ${label} ${kindLabel} 완료`
    };
}

function buildCategoryLookup(categories: TodoDayMarkerCategory[]): Map<string, TodoDayMarkerCategory> {
    const lookup = new Map<string, TodoDayMarkerCategory>();
    if (!Array.isArray(categories)) return lookup;

    for (const category of categories) {
        if (!category) continue;
        if (category.id) lookup.set(category.id, category);
        const nameKey = categoryLookupKey(category.name);
        if (nameKey) lookup.set(nameKey, category);
    }

    return lookup;
}

function findCategoryForItem(
    item: TodoDayMarkerCompletedItem,
    categoryLookup: Map<string, TodoDayMarkerCategory>
): TodoDayMarkerCategory | null {
    if (item.categoryId && categoryLookup.has(item.categoryId)) {
        return categoryLookup.get(item.categoryId) || null;
    }

    const nameKey = categoryLookupKey(item.category);
    if (nameKey && categoryLookup.has(nameKey)) {
        return categoryLookup.get(nameKey) || null;
    }

    return null;
}

function categoryLookupKey(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
}
